// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import TableSettingColumn from "./table-setting-column.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "model_class_name": {
      "column": {
        "default": null,
        "name": "model_class_name",
        "null": false,
        "type": "string"
      },
      "name": "model_class_name",
      "selected_by_default": null,
      "translated": null
    },
    "query_name": {
      "column": {
        "default": null,
        "name": "query_name",
        "null": false,
        "type": "string"
      },
      "name": "query_name",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "TableSetting",
  "collectionKey": "table_settings",
  "collectionName": "table_settings",
  "i18nKey": "table_setting",
  "camelizedLower": "tableSetting",
  "name": "TableSetting",
  "nameDasherized": "table-setting",
  "pluralName": "table_settings",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "TableSettingColumn",
      "collectionName": "table_setting_columns",
      "foreignKey": "table_setting_id",
      "name": "table_setting_columns",
      "macro": "has_many",
      "resource_name": "TableSettingColumn",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "model_class_name",
      "column": {
        "default": null,
        "name": "model_class_name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "query_name",
      "column": {
        "default": null,
        "name": "query_name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "user_id",
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": "uuid"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "TableSettingColumn",
      "collectionName": "table_setting_columns",
      "foreignKey": "table_setting_id",
      "name": "table_setting_columns",
      "macro": "has_many",
      "resource_name": "TableSettingColumn",
      "through": null
    }
  ],
  "paramKey": "table_setting",
  "primaryKey": "id"
}

/** Frontend model for TableSetting. */
export default class TableSetting extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  modelClassName() {
    return this.readAttributeUnderscore("model_class_name")
  }

  /** @returns {boolean} */
  hasModelClassName() {
    const value = this.modelClassName()

    return this._isPresent(value)
  }

  /** @returns {string} */
  queryName() {
    return this.readAttributeUnderscore("query_name")
  }

  /** @returns {boolean} */
  hasQueryName() {
    const value = this.queryName()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./table-setting-column.js").default>} */
  tableSettingColumns() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["table_setting_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "table_setting_columns",
        model: this,
        modelClass: TableSettingColumn
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./table-setting-column.js").default>>} */
  loadTableSettingColumns() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["table_setting_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "table_setting_columns",
        model: this,
        modelClass: TableSettingColumn
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./table-setting-column.js").default>>} */
  ensureTableSettingColumnsLoaded() {
    return this.ensureAssociationLoaded("table_setting_columns")
  }
}
